import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import { PageRequest } from '@/core/request/PageRequest';

import {
  CreateInsurerRequest,
  ListInsurersQuery,
  UpdateInsurerRequest,
} from './Insurers.types';

import {
  listInsurers,
  deleteInsurer,
  createInsurer,
  updateInsurer,
  getInsurer,
  activeInsurer,
  inactiveInsurer,
} from './Insurers.service';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { message, notification } from 'antd';
import { useIntl } from 'react-intl';
import Authorities from '@/types/Authority';
import notificationError from '@/utils/functions/notificationError';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import replaceWords from '@/utils/functions/replaceWords';
import verifyIfHasVinculed from '@/utils/functions/verifyIfHasVinculed';

export function useInsurers(params?: ListInsurersQuery, enabled = true) {
  const query = useInfiniteQuery(
    ['insurers', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listInsurers(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      enabled: enabled,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useInsurer(id: number) {
  const query = useQuery(['getInsurer', id], () => getInsurer(id), {
    enabled: !!id && hasPermissions([Authorities.REGISTER_INSURER_EDIT]),
  });

  useServiceError(query.error);

  return query;
}

export function useInsurerMutation() {
  const mutation = useMutation((id: number) => getInsurer(id));

  useServiceError(mutation.error);

  return mutation;
}

export function useCreateInsurer() {
  const intl = useIntl();
  const mutation = useMutation(
    (obj: CreateInsurerRequest) => createInsurer(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'insurers.create.notification.success',
          }),
          description: intl.formatMessage({
            id: 'insurers.create.notification.successDescription',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateInsurer() {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const mutation = useMutation(
    (obj: UpdateInsurerRequest) => updateInsurer(obj),
    {
      onSuccess() {
        queryClient.invalidateQueries(['getInsurer']);
        notification.success({
          message: intl.formatMessage({
            id: 'insurers.update.notification.success',
          }),
          description: intl.formatMessage({
            id: 'insurers.update.notification.successDescription',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteInsurer() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation((id: number) => deleteInsurer(id), {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'insurers.remove.notification.success',
        }),
        description: intl.formatMessage({
          id: 'insurers.remove.notification.successDescription',
        }),
      });

      queryClient.invalidateQueries('insurers');
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            verifyIfHasVinculed(err) ? replaceWords(err) : err,
          ),
      });
    },
  });

  return mutation;
}

export function useActiveInsurer() {
  const intl = useIntl();

  const mutation = useMutation((id: number) => activeInsurer(id), {
    onSuccess() {
      message.success(
        intl.formatMessage({ id: 'insurers.activate.message.success' }),
      );
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useInactiveInsurer() {
  const intl = useIntl();
  const mutation = useMutation((id: number) => inactiveInsurer(id), {
    onSuccess() {
      message.success(
        intl.formatMessage({ id: 'insurers.inactivate.message.success' }),
      );
    },
  });

  useServiceError(mutation.error);

  return mutation;
}
